import { Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Icon } from './icon';
import { Tilt } from './tilt';
import { Tour } from './data';

@Component({
  selector: 'app-tour-card',
  imports: [RouterLink, Icon, Tilt],
  template: `
    <a
      [routerLink]="['/tours', tour().id]"
      appTilt
      [appTiltMax]="4"
      class="ticket group flex flex-col sm:flex-row sm:h-[324px] bg-paper rounded-[1.75rem] overflow-hidden border border-sand/60 shadow-[0_2px_20px_-8px_rgba(22,19,15,0.15)] hover:shadow-[0_30px_60px_-20px_rgba(22,19,15,0.35)] hover:border-transparent transition-shadow duration-500"
    >
      <!-- Image -->
      <div class="relative sm:w-[42%] aspect-[4/3] sm:aspect-auto overflow-hidden shrink-0">
        <img [src]="tour().image" [alt]="tour().title" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" referrerpolicy="no-referrer" />
        <div class="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent"></div>
        <div class="absolute top-3 left-3 route-code text-[10px] glass-ink text-paper px-2.5 py-1 rounded-full">
          {{ routeLabel() }}
        </div>
        <div class="absolute top-3 right-3 flex items-center gap-1 glass px-2.5 py-1 text-[11px] font-bold rounded-full">
          <app-icon name="star" [size]="11" class="text-saffron" />
          {{ tour().rating }}
        </div>
      </div>

      <!-- Perforated divider -->
      <div class="hidden sm:flex flex-col items-center relative shrink-0 w-px">
        <div class="ticket-notch -mt-[11px] border border-sand/70"></div>
        <div class="ticket-perforation flex-1"></div>
        <div class="ticket-notch -mb-[11px] border border-sand/70"></div>
      </div>
      <div class="sm:hidden relative h-px w-full">
        <div class="ticket-notch -ml-[11px] absolute left-0 top-1/2 -translate-y-1/2 border border-sand/70"></div>
        <div class="h-px w-full bg-transparent" style="background-image: repeating-linear-gradient(to right, var(--color-sand) 0 8px, transparent 8px 16px);"></div>
        <div class="ticket-notch -mr-[11px] absolute right-0 top-1/2 -translate-y-1/2 border border-sand/70"></div>
      </div>

      <!-- Content -->
      <div class="flex-1 min-w-0 p-6 sm:p-7 flex flex-col">
        <div class="flex items-start justify-between gap-3 mb-1">
          <span class="text-[10px] uppercase tracking-[0.2em] font-bold text-teal">{{ tour().region }}</span>
          <span class="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/40 shrink-0">{{ tour().difficulty }}</span>
        </div>
        <h3 class="text-2xl font-serif leading-tight mb-2 line-clamp-2 shrink-0 group-hover:text-vermilion transition-colors">{{ tour().title }}</h3>
        <p class="text-ink/55 text-sm mb-5 leading-relaxed line-clamp-2 shrink-0">{{ tour().tagline }}</p>

        <div class="flex items-center gap-4 text-ink/50 text-xs mb-6">
          <span class="flex items-center gap-1.5"><app-icon name="clock" [size]="13" />{{ tour().duration }}</span>
          <span class="flex items-center gap-1.5"><app-icon name="map" [size]="13" />{{ tour().destinations }}</span>
        </div>

        <div class="mt-auto flex items-end justify-between pt-5 border-t border-dashed border-sand">
          <div>
            <div class="text-[9px] uppercase tracking-widest font-bold text-ink/40 mb-1">From, per person</div>
            <div class="route-code text-2xl text-ink">&#36;{{ tour().price }}</div>
          </div>
          <div class="w-11 h-11 rounded-full flex items-center justify-center bg-ink text-paper group-hover:bg-vermilion group-hover:glow-vermilion transition-all">
            <app-icon name="arrow-right" [size]="18" />
          </div>
        </div>
      </div>
    </a>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class TourCard {
  tour = input.required<Tour>();

  routeLabel() {
    const r = this.tour().route;
    if (r.length <= 1) return r[0] ?? '';
    
    const inner = r.length > 2 && r[0] === r[r.length - 1] ? r.slice(1, -1) : r;
    if (inner.length === 0) return r[0];
    if (inner.length === 1) return inner[0];
    return `${inner[0]} → ${inner[inner.length - 1]}`;
  }
}
